import { useAuth } from '@/providers/AuthProvider';
import {
  useCreateUserMutation,
  useDeleteUserMutation,
  useLazyGetUsersQuery,
  useUpdateUserMutation,
} from '@/store/api/userApi';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { locations } from '@/locations';
import { FieldValues } from 'react-hook-form';
import { useUserDetailsFormMode } from '@/components/pages/SettingsPage/MainContent/shared/UserDetailsForm/useUserDetailsFormMode';
import { useToast } from '@/hooks/shared/use-toast';

export const useUserData = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userId: userIdParam } = useParams();
  const userId = userIdParam || user?.uid;

  const [fetchUsers, { data: userData, isLoading: isLoadingUserData, isFetching: isFetchingUserData }] =
    useLazyGetUsersQuery();
  const [createUserMutation, { isLoading: isCreatingUser }] = useCreateUserMutation();
  const [updateUserMutation, { isLoading: isUpdatingUser }] = useUpdateUserMutation();
  const [deleteUserMutation, { isLoading: isDeletingUser }] = useDeleteUserMutation();

  const { isAccountSettingsMode, isCreateUserMode, isEditUserMode } = useUserDetailsFormMode();

  const userProcessedData = useMemo(
    () =>
      !userData
        ? null
        : {
            ...userData[0],
            uid: userId,
            full_name: `${userData[0].first_name} ${userData[0].last_name}`.trim(),
            name_initials: `${userData[0].first_name[0]} ${userData[0].last_name[0]}`.trim(),
          },
    [userData]
  );

  const deleteUser = async (userId?: any) => {
    if (userId) {
      try {
        await deleteUserMutation(userId);
        navigate(locations.protected.settings.userManagement);
        toast({
          variant: 'success',
          title: 'Success',
          description: 'User successfully deleted',
        });
      } catch (e) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: `Failed to delete user`,
        });
      }
    }
  };

  const createUser = async (values: FieldValues) => {
    try {
      await createUserMutation(values).unwrap();

      toast({
        variant: 'success',
        title: 'Success',
        description: 'User has been created',
      });

      navigate(locations.protected.settings.userManagement);
    } catch (e: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: `Failed to create a new user`,
      });
    }
  };

  const updateUser = async (values: FieldValues) => {
    let updateValues = values;

    if (!values.password) {
      const { password, confirmPassword, assets_allocation, full_name, name_initials, ...restValues } = values;
      updateValues = restValues;
    }

    try {
      await updateUserMutation({ ...updateValues }).unwrap();

      toast({
        variant: 'success',
        title: 'Success',
        description: 'User has been updated',
      });

      if (isAccountSettingsMode) return;

      navigate(locations.protected.settings.userManagement);
    } catch (e: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: `Failed to update user`,
      });
    }
  };

  useEffect(() => {
    fetchUsers({ uids: userId }, true);
  }, [userId]);

  return {
    userId,
    userData: userProcessedData,
    isLoadingUserData: isLoadingUserData || isFetchingUserData,
    createUser,
    updateUser,
    deleteUser,
    isCreatingUser,
    isUpdatingUser,
  };
};
